/**
 * @fileoverview Componente Molecule optimizado para mostrar un carrusel de acciones rápidas con botones personalizados.
 * Utiliza memoización y callbacks para evitar renders innecesarios y mejorar el performance.
 *
 * @module QuickActionsCarouselMC
 * @category Component - Molecule
 * @since 3.1.0
 * @version 3.2.0 - Optimización de renderizado y memoización
 *
 * @typedef {Object} QuickAction
 * @property {string} key - Clave única de la acción
 * @property {string} tooltip - Texto del tooltip
 * @property {function|JSX.Element} [renderContent] - Si existe, se renderiza directamente (ejecutando si es función) y NO se envía a ButtonMC ni se envuelve en botón. Tiene prioridad sobre imageSrc o icon.
 * @property {string|React.ElementType} [icon] - Icono (nombre o componente) a mostrar si no se provee renderContent ni imageSrc
 * @property {string} [imageSrc] - Ruta de imagen a mostrar si no se provee renderContent
 * @property {string} [className] - Clases adicionales para el botón
 * @property {function} [onClick] - Handler de click
 * @property {boolean} [divider] - Si es un divisor visual
 *
 * @typedef {Object} QuickActionsCarouselMCProps
 * @property {QuickAction[]} actions - Lista de acciones rápidas a mostrar
 * @property {string} [carouselClassName] - Clases adicionales para el carrusel
 * @property {number} [itemWidth=40] - Ancho de cada elemento en píxeles
 * @property {number} [maxVisible] - Máximo número de elementos visibles. Si no se especifica, usa valores responsive: 3 (móvil), 7 (desktop)
 * @property {boolean} [useDropdown=true] - Si true, las acciones que no caben van a dropdown con icono "more"
 * @property {boolean} [dropdownUp=true] - Si true, el dropdown se abre hacia arriba; si false, hacia abajo
 *
 * @component QuickActionsCarouselMC
 * @description
 * - Renderiza un carrusel horizontal de acciones rápidas con comportamiento responsive.
 * - Cada acción puede ser un botón, un divisor visual, o un contenido custom si se provee renderContent.
 * - Si existe renderContent, se renderiza directamente (ejecutando si es función) y NO se envía a ButtonMC ni se envuelve en botón.
 * - Usa ButtonMC para los botones estándar y mantiene tooltips y estilos consistentes.
 * - maxVisible responsive: 3 (móvil), 7 (desktop) - puede sobreescribirse con prop maxVisible.
 * - Optimizado con React.memo, useMemo y useCallback para máximo rendimiento.
 *
 * @param {QuickActionsCarouselMCProps} props
 * @returns {JSX.Element}
 *
 * @example
 * // Acción estándar con icono (maxVisible responsive automático)
 * <QuickActionsCarouselMC actions={[{ key: 'edit', icon: EditIcon, tooltip: 'Editar', onClick: () => {} }]} />
 *
 * // Carrusel con maxVisible fijo (sobrescribe responsive)
 * <QuickActionsCarouselMC actions={actions} maxVisible={3} itemWidth={50} />
 *
 * // Carrusel responsive (sin especificar maxVisible) - 3 móvil, 7 desktop
 * <QuickActionsCarouselMC actions={actions} itemWidth={50} />
 *
 * // Dropdown hacia abajo (para componentes en parte superior)
 * <QuickActionsCarouselMC actions={actions} dropdownUp={false} />
 *
 * // Sin dropdown (comportamiento scroll original)
 * <QuickActionsCarouselMC actions={actions} useDropdown={false} />
 *
 * // Acción custom con renderContent (JSX)
 * <QuickActionsCarouselMC actions={[{ key: 'custom', renderContent: <span>Custom</span> }]} />
 *
 * @see ButtonMC
 */
import React, {
  useMemo,
  useCallback,
  memo,
  useState,
  useRef,
  useEffect
} from 'react';
import { ScrollingCarousel } from '@trendyol-js/react-carousel';
import ButtonMC from './ButtonMC';

const QuickActionsCarouselMC = memo(({
  actions,
  carouselClassName = '',
  itemWidth = 40,
  maxVisible,
  useDropdown = true,
  dropdownUp = true
}) => {
  // Estado para controlar el dropdown
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Calcular maxVisible responsive si no se proporciona
  const effectiveMaxVisible = useMemo(() => {
    if (maxVisible !== undefined) return maxVisible;

    // Breakpoints responsivos
    return 7; // Desktop
  }, [maxVisible]);

  /**
   * Memoiza la función para renderizar imágenes si es necesario.
   * @param {string} src
   * @param {string} alt
   * @returns {function}
   */
  const getImageRenderer = useCallback((src, alt) => (
    <img src={src} alt={alt || ''} className="tw-w-[19.89px] tw-h-[20px] tw-object-contain" />
  ), []);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Toggle del dropdown
   */
  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen((prev) => !prev);
  }, []);

  /**
   * Separa las acciones en visibles y para dropdown
   */
  const { visibleActions, dropdownActions } = useMemo(() => {
    if (!useDropdown) {
      return { visibleActions: actions, dropdownActions: [] };
    }

    // Si hay más acciones que effectiveMaxVisible, mostrar effectiveMaxVisible y el resto al dropdown
    if (actions.length > effectiveMaxVisible) {
      return {
        visibleActions: actions.slice(0, effectiveMaxVisible),
        dropdownActions: actions.slice(effectiveMaxVisible)
      };
    }

    // Si hay igual o menos acciones que effectiveMaxVisible, mostrar todas sin dropdown
    return {
      visibleActions: actions,
      dropdownActions: []
    };
  }, [actions, effectiveMaxVisible, useDropdown]);

  /**
   * Maneja el click en una acción, cerrando el dropdown si es necesario
   */
  const handleActionClick = useCallback((action, isInDropdown) => (e) => {
    if (action.onClick) {
      action.onClick(e);
    }
    if (isInDropdown) {
      setIsDropdownOpen(false);
    }
  }, []);

  /**
   * Renderiza una acción individual
   */
  const renderAction = useCallback((action, isInDropdown = false) => {
    // Si existe renderContent, retornar directamente el contenido (ejecutando si es función)
    if (action.renderContent) {
      return typeof action.renderContent === 'function'
        ? action.renderContent()
        : action.renderContent;
    }

    if (action.divider) {
      return <div key={action.key} className="tw-w-[1px] tw-bg-gray-200 tw-mx-2 tw-h-6 tw-self-center" />;
    }

    let renderContent;
    let { icon } = action;
    if (action.imageSrc) {
      renderContent = getImageRenderer(action.imageSrc, action.tooltip);
      icon = undefined;
    }

    const buttonElement = (
      <ButtonMC
        type="button"
        variant={action.variant || 'SECONDARY'}
        size={action.size || 'small'}
        outlined={!!action.className?.includes('tw-border')}
        className={`${action.className || ''} ${isInDropdown ? 'tw-w-full tw-justify-start tw-flex tw-items-center tw-gap-2 tw-text-left tw-text-gray-900' : ''}`}
        data-tooltip-content={action.tooltip}
        onClick={handleActionClick(action, isInDropdown)}
        renderContent={renderContent}
        icon={icon}
        iconPosition={isInDropdown ? 'left' : 'right'}
        iconClassName={isInDropdown ? '!tw-size-[14px]' : action.iconClassName}
        text={isInDropdown ? action.tooltip : undefined}
      />
    );

    if (isInDropdown) {
      return (
        <div key={action.key} className="tw-px-2 tw-py-1">
          {buttonElement}
        </div>
      );
    }

    return (
      <div key={action.key} style={{ minWidth: `${itemWidth}px`, flexShrink: 0 }}>
        {buttonElement}
      </div>
    );
  }, [getImageRenderer, itemWidth, handleActionClick]);

  /**
   * Calcula el ancho máximo del contenedor
   */
  const containerMaxWidth = useMemo(() => {
    const visibleCount = visibleActions.length + (dropdownActions.length > 0 ? 1 : 0); // +1 para botón "more" si hay dropdown
    return `${visibleCount * (itemWidth + 10)}px`; // +10 para margen/padding
  }, [visibleActions.length, dropdownActions.length, itemWidth]);

  return (
    <div className="tw-relative tw-z-20 tw-w-full" style={{ maxWidth: containerMaxWidth }}>
      <ScrollingCarousel
        className={`tw-flex tw-items-center ${carouselClassName}`}
      >
        {visibleActions.map((action) => renderAction(action))}

        {useDropdown && dropdownActions.length > 0 && (
          <div style={{ minWidth: `${itemWidth}px`, flexShrink: 0 }} ref={dropdownRef}>
            <ButtonMC
              type="button"
              variant="SECONDARY"
              size="small"
              className="tw-relative"
              data-tooltip-content="Más acciones"
              onClick={toggleDropdown}
              icon="plus-circle-fill"
              iconClassName="!tw-size-[22px] tw-text-gray-700 hover:tw-text-blue-700 tw-transition-colors tw-duration-150 tw-cursor-pointer"
            />

            {isDropdownOpen && (
              <div
                className={`tw-absolute tw-right-0 tw-z-30 tw-mt-1 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-min-w-[200px] ${dropdownUp ? 'tw-bottom-full tw-mb-1' : 'tw-top-full tw-mt-1'}`}
              >
                <div className="tw-py-2">
                  {dropdownActions.map((action) => renderAction(action, true))}
                </div>
              </div>
            )}
          </div>
        )}
      </ScrollingCarousel>
    </div>
  );
});

export default QuickActionsCarouselMC;
